import { BusinessAreaType } from 'model/Service';

type CalendarFilterByArea = Partial<Record<BusinessAreaType, Array<number>>>;

const sanitizeIds = (ids: unknown): Array<number> => {
    if (!Array.isArray(ids)) {
        return [];
    }

    const uniqueIds = new Set<number>();

    ids.forEach(id => {
        if (typeof id === 'number' && Number.isInteger(id) && id >= 0) {
            uniqueIds.add(id);
        }
    });

    return Array.from(uniqueIds);
};

const readCalendarFilterByArea = (storageKey: string): CalendarFilterByArea => {
    const storedValue = localStorage.getItem(storageKey);
    if (!storedValue) {
        return {};
    }

    try {
        const parsedValue: unknown = JSON.parse(storedValue);
        if (typeof parsedValue !== 'object' || parsedValue === null || Array.isArray(parsedValue)) {
            return {};
        }

        return Object.entries(parsedValue).reduce<CalendarFilterByArea>((accumulator, [businessArea, ids]) => {
            const sanitizedIds = sanitizeIds(ids);
            if (sanitizedIds.length > 0) {
                accumulator[businessArea as BusinessAreaType] = sanitizedIds;
            }

            return accumulator;
        }, {});
    } catch {
        return {};
    }
};

const writeCalendarFilterByArea = (storageKey: string, filtersByArea: CalendarFilterByArea): void => {
    const hasValues = Object.keys(filtersByArea).length > 0;

    if (!hasValues) {
        localStorage.removeItem(storageKey);
        return;
    }

    localStorage.setItem(storageKey, JSON.stringify(filtersByArea));
};

export const normalizeCalendarFilterSelection = (
    selectedIds: Array<number> | undefined,
    availableIds: Array<number>
): Array<number> | undefined => {
    if (selectedIds === undefined) {
        return undefined;
    }

    const sanitizedAvailableIds = sanitizeIds(availableIds);
    if (sanitizedAvailableIds.length === 0) {
        return undefined;
    }

    const sanitizedSelectedIds = sanitizeIds(selectedIds);
    if (sanitizedSelectedIds.length === 0) {
        return undefined;
    }

    const availableIdsSet = new Set(sanitizedAvailableIds);
    const filteredSelectedIds = sanitizedSelectedIds.filter(id => availableIdsSet.has(id));

    if (filteredSelectedIds.length === 0 || filteredSelectedIds.length === sanitizedAvailableIds.length) {
        return undefined;
    }

    return filteredSelectedIds;
};

export const getCalendarFilterSelectionByArea = (
    storageKey: string,
    businessArea: BusinessAreaType
): Array<number> | undefined => {
    const filtersByArea = readCalendarFilterByArea(storageKey);
    return filtersByArea[businessArea];
};

export const setCalendarFilterSelectionByArea = (
    storageKey: string,
    businessArea: BusinessAreaType,
    selectedIds: Array<number> | undefined,
    availableIds: Array<number>
): void => {
    const filtersByArea = readCalendarFilterByArea(storageKey);
    const normalizedSelection = normalizeCalendarFilterSelection(selectedIds, availableIds);

    if (normalizedSelection === undefined) {
        delete filtersByArea[businessArea];
    } else {
        filtersByArea[businessArea] = normalizedSelection;
    }

    writeCalendarFilterByArea(storageKey, filtersByArea);
};

export const removeCalendarFilterSelectionByArea = (storageKey: string, businessArea: BusinessAreaType): void => {
    const filtersByArea = readCalendarFilterByArea(storageKey);
    delete filtersByArea[businessArea];
    writeCalendarFilterByArea(storageKey, filtersByArea);
};
